import React, { useCallback } from 'react'
import {
  saveDepartmentAisleForQueryParams,
  saveDepartmentAisleForRetailerProductIds,
} from 'domains/products/api/editRetailerProductDepartmentAisle'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { DefaultNotificationContent } from 'legacy/components/Notifications/NotificationContents/DefaultNotificationContent'
import { useDomainMessages } from 'domains/products/utils/domain/intl'

interface EditProductsInput {
  partnerId: string
  warehouseId: string
  localeCode: string
  searchTerm?: string
  searchType?: string
  setShowModal: (boolean) => void
  selectedCount: number
  isGlobalSelection: boolean
  selectedKeys: string[]
}

export const useEditProducts = ({
  partnerId,
  warehouseId,
  searchTerm,
  searchType,
  localeCode,
  setShowModal,
  selectedCount,
  isGlobalSelection,
  selectedKeys,
}: EditProductsInput) => {
  const i18n = useDomainMessages(
    {
      successNotificationHeader: 'productsDomain.search.edit.successNotification.header',
      successNotificationContent: 'productsDomain.search.edit.successNotification.content',
      errorNotificationHeader: 'productsDomain.search.edit.errorNotification.header',
      errorNotificationContent: 'productsDomain.search.edit.errorNotification.content',
    },
    { count: selectedCount }
  )

  const { notify, notifyError } = useNotifications()

  return useCallback(
    (departmentId: string, aisleId: string): void => {
      // The departmentId is not needed in this usage.
      // Our backend can identify what the department must be based on the selected
      // aisle. There is a one (department) to many (aisles) relationship, where an
      // aisle can only belong to one department.
      const baseProps = {
        partnerId: partnerId,
        warehouseId: parseInt(warehouseId, 10),
        selectedAisleId: aisleId,
        localeCode: localeCode,
        onFinish: () => {
          //hide the modal for finishing
          setShowModal(false)
        },
        onSuccess: () => {
          notify(
            <DefaultNotificationContent
              header={i18n.successNotificationHeader}
              content={i18n.successNotificationContent}
            />,
            'modern'
          )
        },
        onFailure: () => {
          notifyError(
            <DefaultNotificationContent
              header={i18n.errorNotificationHeader}
              content={i18n.errorNotificationContent}
            />,
            'modern'
          )
        },
      }

      if (isGlobalSelection) {
        saveDepartmentAisleForQueryParams({
          searchTerm: searchTerm,
          searchType: searchType,
          ...baseProps,
        })
      } else {
        saveDepartmentAisleForRetailerProductIds({
          productIds: selectedKeys.map(s => parseInt(s, 10)),
          ...baseProps,
        })
      }
    },
    [
      i18n,
      partnerId,
      warehouseId,
      localeCode,
      isGlobalSelection,
      setShowModal,
      notify,
      notifyError,
      searchTerm,
      searchType,
      selectedKeys,
    ]
  )
}
